import { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { usePlanetStore } from '../store/planetStore';
import { useUniverseStore } from '../store/universeStore';
import { isPlanetOverpopulated, isPlanetOzoneLocked } from '@kaeliux/game-engine';
import type { Planet } from '@kaeliux/shared';
import { ConfirmDialogContext, type DialogOptions } from '../contexts/ConfirmDialogContext';
import ConfirmDialog from './ConfirmDialog';
import GameHUD from './GameHUD';
import AccountNoticeBanner from './AccountNoticeBanner';
import RoundBanner from './RoundBanner';
import { observedBorderBoxHeight } from '../utils/observedBorderBoxHeight';
import PlanetLockOverlay from './PlanetLockOverlay';
import PlanetOverpopulationOverlay from './PlanetOverpopulationOverlay';
import PlanetNavigation from './PlanetNavigation';
import { ToastProvider } from './ui/ToastProvider';
import TutorialOverlay from './TutorialOverlay';
import SpaceBackdrop from './SpaceBackdrop';
import { getSpaceBackdropVariant } from '../utils/spaceBackdropVariant';
import { useGameLoop } from '../hooks/useGameLoop';
import { isTeamPlanetRoute } from '../utils/teamPlanetStyle';

// Lock-overlay confirm/destroy actions are async and keyed to whichever
// planet was gated when the user started them. If PlanetSwitcher moves
// `currentPlanet` to a different planet before one resolves, applying its
// result unconditionally would clobber the switcher's choice with stale data
// for the planet the user isn't even looking at anymore (mirrors the
// same-id guard the background tick effect below already uses).
function applyGatedPlanetUpdate(planet: Planet) {
    const existing = usePlanetStore.getState().currentPlanet;
    if (!existing || existing.id === planet.id) {
        usePlanetStore.setState({ currentPlanet: planet });
    }
}

export default function AppShell() {
    const { user, profile } = useAuthStore();
    const [dialog, setDialog] = useState<DialogOptions | null>(null);
    const hudRef = useRef<HTMLElement>(null);
    const [hudHeight, setHudHeight] = useState(0);
    const navRef = useRef<HTMLElement>(null);
    const [navHeight, setNavHeight] = useState(0);
    const location = useLocation();
    const { loadUniverse, clearSelectedUniverse, selectedUniverseId } = useUniverseStore();
    const { loadPlanet, loadPlanets } = usePlanetStore();
    const currentPlanet = usePlanetStore((s) => s.currentPlanet);
    const activePlanetId = usePlanetStore((s) => s.activePlanetId);
    // The planet the app is viewing/managing: the switcher's active choice,
    // falling back to the profile's homeworld before the switcher has been used.
    const resolvedPlanetId = activePlanetId ?? profile?.mainPlanetId ?? null;

    // Measures the fixed HUD's rendered height so the content below it can be
    // padded by exactly that much, rather than a hard-coded guess — mirrors
    // how the fixed bottom dock (`PlanetNavigation`) accounts for its own height.
    useLayoutEffect(() => {
        const node = hudRef.current;
        if (!node) return;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) setHudHeight(observedBorderBoxHeight(entry, node));
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    // Measures the fixed bottom dock's rendered height the same way, so the
    // spacer below the page content stays in sync with its real height
    // instead of a hard-coded `h-24` guess.
    useLayoutEffect(() => {
        const node = navRef.current;
        if (!node) return;
        const observer = new ResizeObserver((entries) => {
            const entry = entries[0];
            if (entry) setNavHeight(observedBorderBoxHeight(entry, node));
        });
        observer.observe(node);
        return () => observer.disconnect();
    }, []);

    // Re-hydrates the multiplier/sun-age state on a hard reload: the
    // selected universe id itself survives in localStorage, but this
    // store's in-memory multiplier fields don't. If the universe was
    // deleted (or never existed) since it was picked, `loadUniverse` throws
    // — clear the stale pick so the app falls back to the universe picker
    // instead of silently wedging on a universe every gameplay call 400s
    // against (issue #53 review).
    useEffect(() => {
        if (selectedUniverseId) {
            loadUniverse(selectedUniverseId).catch(() => clearSelectedUniverse());
        }
    }, [selectedUniverseId, loadUniverse, clearSelectedUniverse]);

    // Load the player's planet list once so the switcher can enumerate them.
    useEffect(() => {
        if (profile?.id) {
            loadPlanets(profile.id).catch(console.error);
        }
    }, [profile?.id, loadPlanets]);

    useEffect(() => {
        if (profile?.id && resolvedPlanetId) {
            loadPlanet(profile.id, resolvedPlanetId).catch(console.error);
        }
    }, [profile?.id, resolvedPlanetId, loadPlanet]);

    // The app's live game loop — ticks resources, applies completed builds,
    // research, ship upgrades, recycling and fleet arrivals. Mounted here, and
    // above the lock-overlay branch below, so it keeps running on every route
    // (including while a planet is locked, which is how a locked planet
    // recovers on screen).
    useGameLoop(profile?.id, resolvedPlanetId);

    if (!user) {
        return <Navigate to="/login" replace />;
    }

    if (!profile?.race) {
        return <Navigate to="/race" replace />;
    }

    // Match /planet/:id as well as /planet
    const pathnameKey = location.pathname.startsWith('/planet') ? '/planet' : location.pathname;

    const openDialog = (options: DialogOptions) => setDialog(options);
    const closeDialog = () => setDialog(null);

    // /premium (and /personal) aren't about a specific planet, so a locked
    // planet must not hijack them with the rescue overlay — the store is
    // exactly where a player buys Emergency Ozone to fix that lock (issue #99).
    // The Team planet screens show a different planet entirely, one that never
    // locks, so the personal planet's gate doesn't apply there either.
    const isPlanetIndependentRoute = pathnameKey === '/personal'
        || pathnameKey === '/premium'
        || isTeamPlanetRoute(location.pathname);
    const gatedPlanet = currentPlanet && !isPlanetIndependentRoute ? currentPlanet : null;
    const ozoneLocked = gatedPlanet ? isPlanetOzoneLocked(gatedPlanet) : false;
    const overpopulated = gatedPlanet ? isPlanetOverpopulated(profile.race, gatedPlanet) : false;

    return (
        <ConfirmDialogContext.Provider value={{ openDialog }}>
        <ToastProvider>
            <SpaceBackdrop variant={getSpaceBackdropVariant(location.pathname)} />
            <TutorialOverlay />
            <ConfirmDialog
                open={dialog !== null}
                title={dialog?.title ?? ''}
                message={dialog?.message}
                warning={dialog?.warning}
                confirmLabel={dialog?.confirmLabel}
                cancelLabel={dialog?.cancelLabel}
                danger={dialog?.danger}
                onConfirm={() => { dialog?.onConfirm(); closeDialog(); }}
                onCancel={() => { dialog?.onCancel?.(); closeDialog(); }}
            />

            <GameHUD ref={hudRef} />

            <div style={{ paddingTop: hudHeight }}>
                <AccountNoticeBanner />
                <RoundBanner />
                {ozoneLocked || overpopulated ? (
                    <div className="flex flex-col gap-6">
                        {ozoneLocked && (
                            <PlanetLockOverlay
                                planet={gatedPlanet!}
                                onPlanetUpdated={applyGatedPlanetUpdate}
                            />
                        )}
                        {overpopulated && (
                            <PlanetOverpopulationOverlay
                                planet={gatedPlanet!}
                                race={profile.race}
                                onPlanetUpdated={applyGatedPlanetUpdate}
                            />
                        )}
                    </div>
                ) : (
                    <Outlet context={{ hudHeight, navHeight }} />
                )}
            </div>

            {/* Spacer accounting for the fixed bottom nav's height */}
            <div style={{ height: navHeight }} />

            <PlanetNavigation ref={navRef} />
        </ToastProvider>
        </ConfirmDialogContext.Provider>
    );
}
